import { faro } from '@grafana/faro-react'
import { collectDeviceDetails, toFaroAttributes } from 'services/faro/collectDeviceDetails'

export interface FaroClassroomContext {
    userId: string
    classId?: string
    role?: string
}

export interface FaroUserContextSnapshot {
    userId?: string
    classId?: string
}

const currentFaroUserContext: FaroUserContextSnapshot = {}

export const getCurrentFaroUserContext = (): Readonly<FaroUserContextSnapshot> => currentFaroUserContext

const updateCurrentFaroUserContext = (context: FaroClassroomContext): void => {
    currentFaroUserContext.userId = context.userId
    currentFaroUserContext.classId = context.classId
}

const isFaroEnabled = (): boolean => {
    return Boolean(process.env.REACT_APP_FARO_URL && faro.api)
}

let lastSyncedContext: string | null = null
let deviceContextPushed = false

const pushDeviceContextEventOnce = (): boolean => {
    if (!isFaroEnabled() || deviceContextPushed || !faro.api?.pushEvent) {
        return deviceContextPushed
    }

    faro.api.pushEvent('device_context_set', toFaroAttributes(collectDeviceDetails()))
    deviceContextPushed = true
    return true
}

const waitAndPushDeviceContextEvent = async (maxWaitTime = 2000, interval = 100): Promise<void> => {
    const startTime = Date.now()

    while (Date.now() - startTime < maxWaitTime) {
        if (pushDeviceContextEventOnce()) {
            return
        }

        await new Promise(resolve => setTimeout(resolve, interval))
    }
}

export const pushFaroDeviceContextEvent = (): void => {
    if (pushDeviceContextEventOnce()) {
        return
    }

    void waitAndPushDeviceContextEvent()
}

export const syncFaroClassroomContext = (context: FaroClassroomContext): void => {
    if (!isFaroEnabled()) {
        return
    }

    const { userId, classId, role } = context

    const contextKey = `${userId}:${classId ?? ''}:${role ?? ''}`
    if (lastSyncedContext === contextKey) {
        return
    }
    lastSyncedContext = contextKey
    updateCurrentFaroUserContext(context)

    faro.api?.setUser({
        id: userId,
        attributes: {
            ...(classId ? { classId } : {}),
            ...(role ? { role } : {})
        }
    })

    const current = faro.api?.getSession?.()
    if (current?.id) {
        faro.api?.setSession?.({
            id: current.id,
            attributes: {
                ...(classId && { classId }),
                ...(role && { role })
            }
        })
    }

    if (userId && classId && role && faro.api?.pushEvent) {
        faro.api.pushEvent('classroom_context_set', {
            userId,
            classId,
            role
        })
    }
}

export const clearFaroClassroomContext = (): void => {
    if (!isFaroEnabled()) {
        return
    }

    lastSyncedContext = null
    delete currentFaroUserContext.userId
    delete currentFaroUserContext.classId

    faro.api?.setUser(undefined)

    const current = faro.api?.getSession?.()
    if (current?.id && current?.attributes) {
        const { classId, role, ...remainingAttributes } = current.attributes
        faro.api?.setSession?.({
            id: current.id,
            attributes: remainingAttributes
        })
    }
}

export const setFaroView = (pathname: string): void => {
    if (!isFaroEnabled()) return
    const name = pathname.startsWith('/autologin') ? '/autologin/*' : pathname
    faro.api?.setView?.({ name })
}

export const syncFaroClassroomContextFromStorage = (): boolean => {
    if (typeof sessionStorage === 'undefined') {
        return false
    }

    const userId = sessionStorage.getItem('userId')
    if (!userId) {
        return false
    }

    syncFaroClassroomContext({
        userId,
        classId: sessionStorage.getItem('classroomId') ?? undefined,
        role: sessionStorage.getItem('role') ?? undefined
    })

    return true
}

export const resetFaroUserContextStateForTests = (): void => {
    lastSyncedContext = null
    deviceContextPushed = false
    delete currentFaroUserContext.userId
    delete currentFaroUserContext.classId
}
